/**
 * Software Design domain connector manifests.
 *
 * RDM import is the primary stable connector — the RDM parser shipped in
 * PR #1419 already produces a structured document that maps cleanly to the
 * C4/Class/ERD/Sequence/State/Database/Activity/UseCase/ADR/DFD profiles.
 *
 * Code scanner and DB introspection ship as `coming-soon` — the adapters
 * are tracked as separate per-vertical work.
 */
import { registerConnector } from '../../../core/data-profiles/connectorRegistry';

type RegisterArgs = Parameters<typeof registerConnector>[0];

const SWD_CANVAS_DIAGRAM_TYPES = [
  'software-design', 'uml', 'database', 'flowchart', 'generic',
] as const;

const SWD_COMPATIBLE_PROFILES = [
  'c4', 'class', 'erd', 'sequence', 'state',
  'database', 'activity', 'usecase', 'adr', 'dfd', 'component',
  'software-design.journey.test-health',
] as const;

export function registerSoftwareDesignConnectors(): void {
  safeRegister({
    id: 'rdm-import',
    label: 'RDM import',
    description: 'Import an RDM (Rich Diagram Markup) document.',
    iconRef: 'assets/connectors/rdm.svg',
    format: 'file',
    inputHint: '.rdm source file',
    folderKinds: ['software_design', 'workspace'],
    diagramTypes: [...SWD_CANVAS_DIAGRAM_TYPES],
    targets: ['diagram', 'folder', 'both'],
    compatibleProfileIds: [...SWD_COMPATIBLE_PROFILES],
    source: {
      system: 'rdm',
      defaultIntent: 'software_design_graph',
      retentionClass: 'audit_retained',
      volume: 'medium',
    },
    // Status is 'beta' until an RDM→DataRecord[] adapter ships; the RDM
    // parser is live but the adapter wrapper is tracked separately.
    status: 'beta',
    adapter: () => {
      throw new Error(
        '[rdm-import] adapter not yet wired — tracked in M5b-B7 follow-up',
      );
    },
  });

  safeRegister({
    id: 'code-scanner',
    label: 'Code scanner',
    description: 'Scan a repository for services, classes, and relationships.',
    iconRef: 'assets/connectors/code.svg',
    format: 'api-key',
    folderKinds: ['software_design'],
    diagramTypes: [...SWD_CANVAS_DIAGRAM_TYPES],
    targets: ['folder'],
    compatibleProfileIds: [...SWD_COMPATIBLE_PROFILES],
    source: {
      system: 'code_scanner',
      defaultIntent: 'software_design_graph',
      emitsDeltas: true,
      retentionClass: 'rolling_window',
      volume: 'high',
    },
    status: 'coming-soon',
    adapter: () => {
      throw new Error('[code-scanner] not yet implemented');
    },
  });

  safeRegister({
    id: 'db-introspection',
    label: 'Database introspection',
    description: 'Read schema from a live database connection.',
    iconRef: 'assets/connectors/db.svg',
    format: 'api-key',
    folderKinds: ['software_design'],
    diagramTypes: ['software-design', 'database'],
    targets: ['diagram', 'folder'],
    compatibleProfileIds: ['erd', 'database'],
    source: {
      system: 'database',
      defaultIntent: 'software_design_graph',
      emitsDeltas: true,
      retentionClass: 'rolling_window',
      volume: 'high',
    },
    status: 'coming-soon',
    adapter: () => {
      throw new Error('[db-introspection] not yet implemented');
    },
  });

  safeRegister({
    id: 'adr-import',
    label: 'ADR import',
    description: 'Import Architecture Decision Records from a directory.',
    iconRef: 'assets/connectors/adr.svg',
    format: 'file',
    inputHint: 'Folder of Markdown ADR files',
    folderKinds: ['software_design'],
    diagramTypes: ['software-design', 'generic'],
    targets: ['folder'],
    compatibleProfileIds: ['adr'],
    source: {
      system: 'adr',
      defaultIntent: 'software_design_graph',
      retentionClass: 'audit_retained',
      volume: 'medium',
    },
    status: 'coming-soon',
    adapter: () => {
      throw new Error('[adr-import] not yet implemented');
    },
  });
}

function safeRegister(manifest: RegisterArgs): void {
  try {
    registerConnector(manifest);
  } catch (err) {
    if (err instanceof Error && err.message.includes('duplicate connector id')) return;
    throw err;
  }
}
